import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { format } from "date-fns";
import {
  ArrowLeft, Phone, MessageCircle, Globe, Instagram, Facebook, Linkedin, Building2,
  MapPin, User, CalendarClock, Sparkles, Loader2, Wand2, Check, Trash2, RefreshCw, Image as ImageIcon, Pencil,
  Mail, Cake, Briefcase, FileText, Upload, Flag, Hash, BookUser, UserPlus, Tag,
} from "lucide-react";
import { toast } from "sonner";
import { api, apiError, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge } from "@/components/StatusBadge";
import { Avatar } from "@/components/Avatar";
import { ScreenshotUploader } from "@/components/ScreenshotUploader";
import { CustomerForm } from "@/components/CustomerForm";
import { STATUS_META, STATUS_ORDER } from "@/lib/status";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";

const LinkChip = ({ href, icon: Icon, label }) => {
  if (!href) return null;
  const url = href.startsWith("http") ? href : `https://${href}`;
  return (
    <a
      data-testid={`link-${label.toLowerCase()}`}
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1.5 text-sm border border-border rounded-full px-3 py-1.5 hover:bg-secondary hover:border-primary/40 transition-colors"
    >
      <Icon className="h-3.5 w-3.5" /> {label}
    </a>
  );
};

const InfoRow = ({ icon: Icon, label, value, href }) => (
  <div className="flex items-start gap-3 py-2.5">
    <Icon className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
    <div className="min-w-0">
      <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</div>
      {href ? (
        <a href={href} target="_blank" rel="noopener noreferrer" className="text-sm font-medium hover:underline break-all">{value || "—"}</a>
      ) : (
        <div className="text-sm font-medium break-words">{value || "—"}</div>
      )}
    </div>
  </div>
);

export default function CustomerProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [customer, setCustomer] = useState(null);
  const [updateOpen, setUpdateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [consultants, setConsultants] = useState([]);
  const [uploadingPassport, setUploadingPassport] = useState(false);
  const passportInputRef = useRef();

  const uploadPassport = async (file) => {
    if (!file) return;
    setUploadingPassport(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/customers/${id}/passport`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Passport read & attached");
      load();
    } catch (e) {
      toast.error(apiError(e, "Could not process passport"));
    } finally {
      setUploadingPassport(false);
    }
  };

  useEffect(() => {
    if (user?.role === "admin") api.get("/users").then((r) => setConsultants(r.data)).catch(() => {});
  }, [user]);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/customers/${id}`);
      setCustomer(r.data);
    } catch (e) {
      toast.error(apiError(e, "Could not load customer"));
      navigate("/customers");
    }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  const changeStatus = async (status) => {
    await api.patch(`/customers/${id}`, { status });
    toast.success(`Marked as ${STATUS_META[status].label}`);
    load();
  };

  const changeFollowup = async (d) => {
    const val = d ? format(d, "yyyy-MM-dd") : null;
    await api.patch(`/customers/${id}`, { next_followup_date: val });
    toast.success("Follow-up date updated");
    load();
  };

  const remove = async () => {
    await api.delete(`/customers/${id}`);
    toast.success("Customer deleted");
    navigate("/customers");
  };

  if (!customer) return <div className="p-10 text-muted-foreground">Loading…</div>;

  return (
    <div className="p-8 max-w-[1200px] animate-in-up">
      <button
        data-testid="profile-back"
        onClick={() => navigate(-1)}
        className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-6"
      >
        <ArrowLeft className="h-4 w-4" /> Back
      </button>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-8">
        <Avatar name={customer.name || "Unknown"} size={60} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="font-display text-3xl font-bold tracking-tight">{customer.name || "Unnamed lead"}</h1>
            {(customer.contact_type && customer.contact_type !== "Customer") && (
              <span data-testid="contact-type-badge" className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold bg-secondary text-foreground">
                <Tag className="h-3 w-3" /> {customer.contact_type}
              </span>
            )}
            {customer.contact_type !== "Team Member" && <StatusBadge status={customer.status} />}
          </div>
          <p className="text-muted-foreground mt-0.5">
            {[customer.company, customer.country].filter(Boolean).join(" · ") || "No company"}
          </p>
        </div>
        <div className="flex gap-2">
          <Button data-testid="edit-customer-button" variant="outline" onClick={() => setEditOpen(true)} className="gap-2 font-semibold">
            <Pencil className="h-4 w-4" /> Edit Details
          </Button>
          <Button data-testid="update-customer-button" onClick={() => setUpdateOpen(true)} className="gap-2 font-semibold">
            <RefreshCw className="h-4 w-4" /> Update Customer
          </Button>
          {(user?.role === "admin" || customer.consultant_id === user?.id) && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button data-testid="delete-customer-button" variant="outline" size="icon"><Trash2 className="h-4 w-4" /></Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete this customer?</AlertDialogTitle>
                  <AlertDialogDescription>This permanently removes {customer.name} and all activity. This cannot be undone.</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction data-testid="confirm-delete" onClick={remove} className="bg-destructive hover:bg-destructive/90">Delete</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: details */}
        <div className="lg:col-span-1 space-y-6">
          {/* Quick controls */}
          {customer.contact_type !== "Team Member" && (
          <div className="bg-white border border-border rounded-lg p-5 space-y-4">
            <div>
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1.5">Lead Status</div>
              <Select value={customer.status} onValueChange={changeStatus}>
                <SelectTrigger data-testid="profile-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STATUS_ORDER.map((s) => (
                    <SelectItem key={s} value={s}>
                      <span className="flex items-center gap-2">
                        <span className="h-2 w-2 rounded-full" style={{ background: STATUS_META[s].color }} />
                        {STATUS_META[s].label}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1.5">Next Follow-up</div>
              <Popover>
                <PopoverTrigger asChild>
                  <Button data-testid="profile-followup-select" variant="outline" className="w-full justify-start font-normal">
                    <CalendarClock className="mr-2 h-4 w-4" />
                    {customer.next_followup_date || <span className="text-muted-foreground">No date set</span>}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar mode="single" selected={customer.next_followup_date ? new Date(customer.next_followup_date) : undefined} onSelect={changeFollowup} />
                </PopoverContent>
              </Popover>
            </div>
          </div>
          )}

          <div className="bg-white border border-border rounded-lg p-5 divide-y divide-border">
            <InfoRow icon={Phone} label="Phone" value={customer.phone} href={customer.phone ? `tel:${customer.phone}` : null} />
            <InfoRow icon={MessageCircle} label="WhatsApp" value={customer.whatsapp} href={customer.whatsapp ? `https://wa.me/${(customer.whatsapp || "").replace(/[^0-9]/g, "")}` : null} />
            <InfoRow icon={Building2} label="Company" value={customer.company} />
            <InfoRow icon={MapPin} label="Country" value={customer.country} />
            <InfoRow icon={Sparkles} label="Product Interest" value={customer.product_interest} />
            <InfoRow icon={User} label="Responsible Consultant" value={customer.consultant_name} />
            <InfoRow icon={Globe} label="Lead Source" value={customer.lead_source} />
            {customer.sub_agent ? <InfoRow icon={UserPlus} label="Sub-Agent" value={customer.sub_agent} /> : null}
            {customer.pi_number ? <InfoRow icon={FileText} label="PI Number" value={customer.pi_number} /> : null}
          </div>

          {/* Personal & Passport */}
          <div className="bg-white border border-border rounded-lg p-5">
            <p className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground mb-1">Personal & Passport</p>
            <div className="divide-y divide-border">
              <InfoRow icon={Cake} label="Date of Birth" value={customer.date_of_birth} />
              <InfoRow icon={Flag} label="Nationality" value={customer.nationality} />
              <InfoRow icon={Hash} label="Passport Number" value={customer.passport_number} />
              <InfoRow icon={CalendarClock} label="Passport Issue" value={customer.passport_issue_date} />
              <InfoRow icon={CalendarClock} label="Passport Expiry" value={customer.passport_expiry_date} />
              <InfoRow icon={Briefcase} label="Designation" value={customer.designation} />
              {customer.position ? <InfoRow icon={Briefcase} label="Position" value={customer.position} /> : null}
              {customer.department ? <InfoRow icon={Tag} label="Department" value={customer.department} /> : null}
              <InfoRow icon={Mail} label="Email" value={customer.email} href={customer.email ? `mailto:${customer.email}` : null} />
              <InfoRow icon={MapPin} label="State / Province" value={customer.state} />
              <InfoRow icon={MapPin} label="City" value={customer.city} />
            </div>
            {customer.notes && (
              <div className="mt-3 pt-3 border-t border-border">
                <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1">Notes</div>
                <p className="text-sm leading-relaxed">{customer.notes}</p>
              </div>
            )}
          </div>

          {/* Documents */}
          <div className="bg-white border border-border rounded-lg p-5">
            <div className="flex items-center justify-between mb-3">
              <p className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground">Documents</p>
              <input ref={passportInputRef} type="file" accept="image/*,application/pdf" hidden onChange={(e) => { uploadPassport(e.target.files?.[0]); e.target.value = ""; }} />
              <button
                data-testid="upload-passport-button"
                onClick={() => passportInputRef.current?.click()}
                disabled={uploadingPassport}
                className="text-xs font-semibold inline-flex items-center gap-1.5 border border-border rounded-full px-3 py-1.5 hover:bg-secondary transition-colors disabled:opacity-50"
              >
                {uploadingPassport ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />} Upload Passport
              </button>
            </div>
            {customer.documents?.length ? (
              <div className="space-y-2">
                {customer.documents.map((d) => (
                  <a
                    key={d.id}
                    data-testid={`document-${d.id}`}
                    href={fileUrl(d.path)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 rounded-md border border-border px-3 py-2.5 hover:bg-secondary transition-colors"
                  >
                    <div className="h-8 w-8 rounded-md bg-secondary flex items-center justify-center shrink-0"><BookUser className="h-4 w-4" /></div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium truncate">{d.filename}</div>
                      <div className="text-[11px] text-muted-foreground">By {d.uploaded_by_name} · {d.uploaded_at ? format(new Date(d.uploaded_at), "MMM d, yyyy") : ""}</div>
                    </div>
                    <FileText className="h-4 w-4 text-muted-foreground" />
                  </a>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No documents yet. Upload a passport to attach one.</p>
            )}
          </div>

          {(customer.instagram || customer.facebook || customer.linkedin || customer.website) && (
            <div className="flex flex-wrap gap-2">
              <LinkChip href={customer.instagram} icon={Instagram} label="Instagram" />
              <LinkChip href={customer.facebook} icon={Facebook} label="Facebook" />
              <LinkChip href={customer.linkedin} icon={Linkedin} label="LinkedIn" />
              <LinkChip href={customer.website} icon={Globe} label="Website" />
            </div>
          )}
        </div>

        {/* Right: summary + timeline */}
        <div className="lg:col-span-2 space-y-6">
          {customer.summary && (
            <div className="bg-white border border-border rounded-lg p-5">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="h-4 w-4" />
                <h3 className="font-display font-semibold text-sm tracking-tight">Latest Summary</h3>
              </div>
              <p className="text-sm leading-relaxed text-foreground/90">{customer.summary}</p>
            </div>
          )}

          <div className="bg-white border border-border rounded-lg overflow-hidden">
            <div className="px-5 py-3.5 border-b border-border">
              <h3 className="font-display font-semibold text-sm tracking-tight">Activity Timeline</h3>
            </div>
            <div className="p-5 space-y-5">
              {customer.activities?.length ? customer.activities.map((a) => (
                <div key={a.id} data-testid={`activity-${a.id}`} className="flex gap-3">
                  <div className="flex flex-col items-center">
                    <div className="h-8 w-8 rounded-full bg-secondary flex items-center justify-center shrink-0">
                      {a.type === "created" ? <Sparkles className="h-4 w-4" /> : <RefreshCw className="h-4 w-4" />}
                    </div>
                    <div className="w-px flex-1 bg-border mt-1" />
                  </div>
                  <div className="pb-2 min-w-0 flex-1">
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <span className="font-semibold text-foreground">{a.user_name}</span>
                      <span>·</span>
                      <span>{a.created_at ? format(new Date(a.created_at), "MMM d, yyyy · h:mm a") : ""}</span>
                    </div>
                    <p className="text-sm mt-1 leading-relaxed">{a.summary}</p>
                    {a.screenshots?.length > 0 && (
                      <div className="flex gap-2 mt-2 flex-wrap">
                        {a.screenshots.map((p, i) => (
                          <a key={i} href={fileUrl(p)} target="_blank" rel="noopener noreferrer" className="h-16 w-16 rounded-md overflow-hidden border border-border hover:opacity-80 transition-opacity">
                            <img src={fileUrl(p)} alt="screenshot" className="h-full w-full object-cover" />
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )) : (
                <div className="text-sm text-muted-foreground text-center py-6">No activity yet.</div>
              )}
            </div>
          </div>
        </div>
      </div>

      <UpdateDialog open={updateOpen} setOpen={setUpdateOpen} customer={customer} onDone={load} />
      <EditDialog open={editOpen} setOpen={setEditOpen} customer={customer} onDone={load} isAdmin={user?.role === "admin"} consultants={consultants} />
    </div>
  );
}

function EditDialog({ open, setOpen, customer, onDone, isAdmin, consultants }) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({
        name: customer.name || "", company: customer.company || "", phone: customer.phone || "",
        whatsapp: customer.whatsapp || "", country: customer.country || "", lead_source: customer.lead_source || "",
        product_interest: customer.product_interest || "", summary: customer.summary || "", status: customer.status,
        next_followup_date: customer.next_followup_date || "", instagram: customer.instagram || "",
        facebook: customer.facebook || "", linkedin: customer.linkedin || "", website: customer.website || "",
        date_of_birth: customer.date_of_birth || "", nationality: customer.nationality || "",
        passport_number: customer.passport_number || "", passport_issue_date: customer.passport_issue_date || "",
        passport_expiry_date: customer.passport_expiry_date || "", designation: customer.designation || "",
        email: customer.email || "", city: customer.city || "", notes: customer.notes || "",
        contact_type: customer.contact_type || "Customer", state: customer.state || "",
        sub_agent: customer.sub_agent || "", pi_number: customer.pi_number || "",
        position: customer.position || "", department: customer.department || "",
        consultant_id: customer.consultant_id || "",
      });
    }
  }, [open, customer]);

  const save = async () => {
    if (!form.name.trim()) { toast.error("Customer name is required"); return; }
    setSaving(true);
    try {
      const payload = { ...form };
      delete payload.screenshots;
      if (!isAdmin) delete payload.consultant_id;
      if (!payload.next_followup_date) payload.next_followup_date = null;
      await api.patch(`/customers/${customer.id}`, payload);
      toast.success("Customer details updated");
      setOpen(false);
      onDone();
    } catch (e) {
      toast.error(apiError(e, "Could not save"));
    } finally {
      setSaving(false);
    }
  };

  if (!form) return null;
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">Edit Customer Details</DialogTitle>
        </DialogHeader>
        <CustomerForm form={form} setForm={setForm} consultants={consultants} isAdmin={isAdmin} showConsultant={isAdmin} />
        <DialogFooter>
          <Button data-testid="edit-save-button" onClick={save} disabled={saving} className="gap-2 font-semibold">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function UpdateDialog({ open, setOpen, customer, onDone }) {
  const [step, setStep] = useState("upload");
  const [mode, setMode] = useState("ai"); // ai | manual
  const [manualText, setManualText] = useState("");
  const [files, setFiles] = useState([]);
  const [summary, setSummary] = useState("");
  const [status, setStatus] = useState(customer.status);
  const [followup, setFollowup] = useState(customer.next_followup_date || "");
  const [screenshots, setScreenshots] = useState([]);
  const [saving, setSaving] = useState(false);

  const reset = () => { setStep("upload"); setMode("ai"); setManualText(""); setFiles([]); setSummary(""); setScreenshots([]); setStatus(customer.status); setFollowup(customer.next_followup_date || ""); };

  const process = async () => {
    if (files.length === 0) { toast.error("Upload at least one screenshot"); return; }
    setStep("processing");
    const fd = new FormData();
    files.forEach((f) => fd.append("files", f));
    fd.append("mode", "update");
    fd.append("customer_id", customer.id);
    try {
      const r = await api.post("/ai/extract", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setSummary(r.data.summary || "");
      if (r.data.suggested_status) setStatus(r.data.suggested_status);
      if (r.data.suggested_followup_date) setFollowup(r.data.suggested_followup_date);
      setScreenshots(r.data.screenshots || []);
      setStep("review");
    } catch (e) {
      toast.error(apiError(e, "AI failed"));
      setStep("upload");
    }
  };

  const save = async () => {
    if (!summary.trim()) { toast.error("Summary is required"); return; }
    setSaving(true);
    try {
      await api.post(`/customers/${customer.id}/activity`, { summary, screenshots, status, next_followup_date: followup || null });
      await api.patch(`/customers/${customer.id}`, { summary });
      toast.success("Customer updated");
      setOpen(false);
      reset();
      onDone();
    } catch (e) {
      toast.error(apiError(e, "Could not save"));
    } finally {
      setSaving(false);
    }
  };

  const saveManual = async () => {
    if (!manualText.trim()) { toast.error("Please type a communication update"); return; }
    setSaving(true);
    try {
      await api.post(`/customers/${customer.id}/activity`, {
        summary: manualText.trim(), screenshots: [], status, next_followup_date: followup || null,
      });
      toast.success("Manual update saved");
      setOpen(false);
      reset();
      onDone();
    } catch (e) {
      toast.error(apiError(e, "Could not save"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">Update {customer.name}</DialogTitle>
        </DialogHeader>

        {step === "upload" && (
          <div className="space-y-4">
            {/* Mode toggle */}
            <div className="grid grid-cols-2 gap-2 p-1 bg-secondary rounded-lg">
              <button
                data-testid="update-mode-ai"
                onClick={() => setMode("ai")}
                className={`text-xs font-semibold rounded-md py-2 transition-colors flex items-center justify-center gap-1.5 ${mode === "ai" ? "bg-white shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
              >
                <Wand2 className="h-3.5 w-3.5" /> Screenshots + AI
              </button>
              <button
                data-testid="update-mode-manual"
                onClick={() => setMode("manual")}
                className={`text-xs font-semibold rounded-md py-2 transition-colors flex items-center justify-center gap-1.5 ${mode === "manual" ? "bg-white shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
              >
                <Pencil className="h-3.5 w-3.5" /> Manual Update
              </button>
            </div>

            {mode === "ai" ? (
              <>
                <p className="text-sm text-muted-foreground">Upload new conversation screenshots. AI will summarize and suggest a follow-up.</p>
                <ScreenshotUploader files={files} setFiles={setFiles} />
                <Button data-testid="update-process-button" onClick={process} className="w-full gap-2 font-semibold"><Wand2 className="h-4 w-4" /> Process with AI</Button>
              </>
            ) : (
              <div className="space-y-4">
                <div>
                  <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5">Communication Update</div>
                  <Textarea data-testid="manual-update-text" value={manualText} onChange={(e) => setManualText(e.target.value)} rows={5}
                    placeholder="Type or paste the customer communication update…" className="resize-none" />
                  <p className="text-[11px] text-muted-foreground mt-1">Saved directly — no AI used. Your name, date and time are recorded automatically.</p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5">Status <span className="normal-case text-[10px]">(optional)</span></div>
                    <Select value={status} onValueChange={setStatus}>
                      <SelectTrigger data-testid="manual-update-status"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {STATUS_ORDER.map((s) => <SelectItem key={s} value={s}>{STATUS_META[s].label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5">Follow-up <span className="normal-case text-[10px]">(optional)</span></div>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button data-testid="manual-update-followup" variant="outline" className="w-full justify-start font-normal text-sm">
                          <CalendarClock className="mr-2 h-4 w-4" />{followup || <span className="text-muted-foreground">Date</span>}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar mode="single" selected={followup ? new Date(followup) : undefined} onSelect={(d) => setFollowup(d ? format(d, "yyyy-MM-dd") : "")} />
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>
                <DialogFooter>
                  <Button data-testid="save-manual-update-button" onClick={saveManual} disabled={saving} className="gap-2 font-semibold">
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Save Manual Update
                  </Button>
                </DialogFooter>
              </div>
            )}
          </div>
        )}

        {step === "processing" && (
          <div className="py-16 flex flex-col items-center gap-3">
            <Sparkles className="h-10 w-10 animate-pulse" />
            <p className="text-sm text-muted-foreground flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Reading conversation…</p>
          </div>
        )}

        {step === "review" && (
          <div className="space-y-4">
            <div>
              <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5">Summary</div>
              <Textarea data-testid="update-summary" value={summary} onChange={(e) => setSummary(e.target.value)} rows={4} className="resize-none" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5">Status</div>
                <Select value={status} onValueChange={setStatus}>
                  <SelectTrigger data-testid="update-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STATUS_ORDER.map((s) => <SelectItem key={s} value={s}>{STATUS_META[s].label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5">Follow-up</div>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button data-testid="update-followup" variant="outline" className="w-full justify-start font-normal text-sm">
                      <CalendarClock className="mr-2 h-4 w-4" />{followup || <span className="text-muted-foreground">Date</span>}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar mode="single" selected={followup ? new Date(followup) : undefined} onSelect={(d) => setFollowup(d ? format(d, "yyyy-MM-dd") : "")} />
                  </PopoverContent>
                </Popover>
              </div>
            </div>
            <DialogFooter>
              <Button data-testid="update-save-button" onClick={save} disabled={saving} className="gap-2 font-semibold">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Save Update
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
